/**
 * 企业微信的相关功能，目前跟三局 **企业微信私有部署** 关联较大，其他租户需要使用另说
 * 如果需要企业微信js-sdk，建议直接在head里直接引用jwxwork-1.0.0.js文件，在另一个项目里试过，魔改不动了
 */
import { Toast } from 'react-vant';
import { generateAppJSSDKSignature, generateCorpJSSDKSignature, getWxSdkConfig } from 'api/qywx';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { ENV } from './urls';

/**
 * 企业微信ua中有 wxwork/x.x.x
 * 三局通（私有化版本）ua中有 wxworklocal/x.x.x
 * @param ua user agent
 * @returns {boolean}
 */
export function isQywx(ua: string): boolean {
  return ua ? ua.includes('wxwork') : false;
}

export function isWx(ua: string): boolean {
  return ua.includes('MicroMessenger');
}

// 新的微信小程序环境判断方式，isWx并不准确，企业微信的ua同样包含MicroMessenger
export function isWxNew(): boolean {
  return window?.__wxjs_environment === 'miniprogram';
}

export type SemVer = {
  major: number;
  minor: number;
  patch: number;
};

/**
 * 企业微信的 jssdk 没得取系统信息的 api，只有自己判断 ua
 * eg:
 * 0.221 0.221 222.212.90.149 - 10.8.12.21:30802 [30/Aug/2022:15:43:19 +0800] cscec3b-m.tabe.cn "GET /api/tender/v1/mobile/supplier/home/statistics HTTP/1.1" 200 520 "https://cscec3b-m.tabe.cn/mine" "Mozilla/5.0 (Linux; Android 9; SM-N9500 Build/PPR1.180610.011; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/77.0.3865.120 MQQBrowser/6.2 TBS/045709 Mobile Safari/537.36 wxworklocal/2.6.590000 wwlocal/2.6.590000 wxwork/3.0.0 appname/wxworklocal-customized MicroMessenger/7.0.1 appScheme/wxworklocalcustomized" "-" "-" - "10.8.12.65" "-""-""222ffd42db4f42c1941888ae7da1e20e.23010.16618453993285981"
 */
export function qywxSemVer(ua: string): SemVer | null {
  if (!ua) {
    return null;
  }

  const matches = ua.match(/wxworklocal\/(\d+)\.(\d+).(\d+)/);
  if (matches.length !== 4) {
    return null;
  }

  // matches[0] is wxworklocal/2.6.590000
  return {
    major: +matches[1],
    minor: +matches[2],
    patch: +matches[3],
  };
}

export function newerOrEqual(source: SemVer, target: SemVer): boolean {
  if (source.major > target.major) {
    return true;
  } else if (source.major === target.major) {
    if (source.minor > target.minor) {
      return true;
    } else if (source.minor === target.minor) {
      return source.patch >= target.patch;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 * 企业微信中的登录需要走OAuth2流程：https://work.weixin.qq.com/api/doc/90000/90135/91335
 * 跟微信不同的是，后台登录后拿到的userId已经可以确定用户，set-cookie了，所以不需要后续的sso/login和绑定了
 * @returns {boolean} isLogin
 */
export function useQywxLogin() {
  const [isLogin, setIsLogin] = useState(false);
  const router = useRouter();
  // FIXME
  const qywxToken = useRef(router.query.qywxToken as string);

  useEffect(() => {
    if (!isQywx(window.navigator.userAgent)) return;

    if (qywxToken.current !== null) {
      // jump back from oauth
      sessionStorage.setItem('qywxToken', qywxToken.current);
      setIsLogin(true);
    } else if (!!sessionStorage.getItem('qywxToken')) {
      // already login
      setIsLogin(true);
    } else {
      // should jump
      // FIXME
      const bindUrl = '/api/ifs/v1/wx/wxMpBind';
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `${origin}${bindUrl}?returnUrl=${returnUrl}`;
    }
  }, []);

  return isLogin;
}

const jsApiList = ['invoke', 'openEnterpriseApp', 'launchMiniprogram', 'getLocation']; //必填，传入需要使用的接口名称

function log(_sth) {
  // const div = document.createElement("div")
  // div.appendChild(document.createTextNode(_sth))
  // div.style.paddingBottom = "100px"
  // document.body.appendChild(div)
  console.warn(_sth);
}

let lastWxUrl = null;

/**
 * 初始化微信sdk
 * 前端路由不需要重复初始化，初始化了在 iOS 里反而报错
 */
function initWxSdk() {
  if (lastWxUrl !== null) {
    return Promise.resolve();
  }

  const url = document.location.href;
  lastWxUrl = url;
  console.info('初始化微信JSSDK');
  return getWxSdkConfig(url).then((res) => {
    if (res.code === 200) {
      log('config param: ' + JSON.stringify(res.data));
      wx.config({
        ...res.data,
        // debug: debugEnv() === "dev",
        jsApiList: ['checkJsApi', 'getLocation'], // 必填，需要使用的JS接口列表
        openTagList: ['wx-open-launch-weapp'], // 可选，需要使用的开放标签列表，例如['wx-open-launch-app']
      });
      return new Promise<void>((resolve, reject) => {
        wx.ready(() => {
          resolve();
        });
        wx.error((err) => {
          reject(err);
        });
      });
    } else {
      return Promise.reject(res.message);
    }
  });
}

/**
 * 初始化企业微信sdk
 * 同一个 url ，不需要两次初始化，切换 url 必须重新初始化
 * @param agentId h5 应用的 agentId
 */
function initQywxSdk(agentId: string) {
  const url = document.location.href;
  return generateCorpJSSDKSignature(agentId, url)
    .then((res) => {
      log('sig res: ' + JSON.stringify(res));
      if (res.code === 200) {
        wx.config({
          ...res.data,
          // debug: debugEnv() === "dev",
          beta: true,
          jsApiList,
        });
        return new Promise<void>((resolve, reject) => {
          wx.ready(() => {
            resolve();
          });
          wx.error((err) => {
            reject(err);
          });
        });
      } else {
        return Promise.reject(res.message);
      }
    })
    .then(() => {
      return generateAppJSSDKSignature(agentId, url);
    })
    .then((res) => {
      log('sig res2: ' + JSON.stringify(res));
      if (res.code === 200) {
        return new Promise((resolve, reject) => {
          wx.invoke(
            'agentConfig',
            {
              ...res.data,
              timestamp: +res.data.timestamp, // 微信是 number | string，企业微信只能是 number
              corpid: res.data.appId, // 必填，企业微信的corpid，必须与当前登录的企业一致
              agentid: agentId, // 必填，企业微信的应用id （e.g. 1000247）
              jsApiList,
            },
            function (res) {
              if (res.err_msg === 'agentConfig:ok') {
                //这里可以调用用户数据接口
                log('agentconfig suc: ' + JSON.stringify(res));
                // 回调
                resolve(res);
              } else {
                //错误处理
                log('agentconfig fail: ' + JSON.stringify(res));
                reject(res.err_msg);
              }
            },
          );
        });
      } else {
        log('http err: ' + res.message);
        return Promise.reject(res.message);
      }
    });
}

/**
 * 三局通这个版本之后的才支持拉起小程序传参数
 */
const SUPP_LAUNCH_MIN_VER: SemVer = {
  major: 2,
  minor: 6,
  patch: 590000,
};

function launchMiniProgram(agentId: number, extended_param: string, path: string) {
  return new Promise<any>((resolve, reject) => {
    const ua = window.navigator.userAgent;
    const semver = qywxSemVer(ua);
    // only works above 2.6.
    if (newerOrEqual(semver, SUPP_LAUNCH_MIN_VER)) {
      // 这个 api 的参数的 `agentId`，
      // 在 Android 上可以传 `string | number`，在 iOS 上只能传 `string`，不然报错
      wx.invoke(
        'launchMiniprogram',
        {
          agentid: agentId.toString(), // 需跳转的小程序appid
          path: `${path}?${extended_param}`, // 所需跳转的小程序内页面路径及参数。非必填
        },
        function (res) {
          if (res.err_msg === 'launchMiniprogram:ok') {
            // 正常
            resolve(res);
          } else {
            // 错误处理
            reject(res.err_msg);
          }
        },
      );
    } else {
      wx.invoke(
        'openEnterpriseApp',
        {
          agentid: agentId, //去管理平台对应的应用设置界面查询
          extended_param, //可选，这个参数作为拼装到应用URL的随机印证，由应用方根据业务场景和需要选择性入参，一般不用。
        },
        function (res) {
          if (res.err_msg === 'openEnterpriseApp:ok') {
            resolve(res);
          } else {
            // 错误处理
            reject(res.err_msg);
          }
        },
      );
    }
  });
}

/**
 * 在企业微信中拉起云收验货小程序，需要自行initSdk
 */
export async function launchIBuildMiniProgram(param: string, path = 'pages/index/index') {
  const { ibuildAgentId } = getQywxConfig();
  log(`ibuildAgentId: ${ibuildAgentId} para: ${param}`);
  try {
    const res2 = await launchMiniProgram(ibuildAgentId, param, path);
    log('launchIBuildMiniProgram:' + JSON.stringify(res2));
    return [true, null];
  } catch (error) {
    log('error: ' + JSON.stringify(error));
    return [false, error];
  }
}

// TODO appollorize
const configs = {
  dev: {
    host: 'qw.cscec3b.com.cn',
    appid: 'wwdd4ee271fd6be185', // 物资工匠
    redirect_uri: 'm.3jyx.cn%2Fsso%2Fauthorize%2Fwechat',
    agentid: '1000183',
    ibuildAgentId: 1000126,
    ibuildUserName: 'gh_cbeb4149cb80',
    yxIbuildAgentId: 1000351, // 严选商城在三局通中标识
  },
  ga: {
    host: 'qw.cscec3b.com.cn',
    appid: 'wwdd4ee271fd6be185',
    // FIXME
    redirect_uri: 'm.3jyx.cn%2Fsso%2Fauthorize%2Fwechat', //会重定向至 m.3jyx.com
    agentid: '1000183', // 用于初始化qywx sdk，与域名绑定在一块
    ibuildAgentId: 1000126, // 物资工匠，云收货小程序在三局通中标识
    ibuildUserName: 'gh_cbeb4149cb80', //物资工匠，云收货小程序在三局通中标识
    yxIbuildAgentId: 1000351, // 严选商城在三局通中标识
  },
};

export function getQywxConfig() {
  const env = debugEnv();
  const config = configs[env];
  return config;
}

function debugEnv() {
  return ['zjdev', 'zjst'].includes(ENV) ? 'dev' : 'ga';
}

export async function initSdk(): Promise<'wx' | 'qywx' | 'other'> {
  const ua = window.navigator.userAgent;
  if (isQywx(ua)) {
    const { agentid } = getQywxConfig();
    await initQywxSdk(agentid);
    return 'qywx';
  } else if (isWx(ua)) {
    await initWxSdk();
    return 'wx';
  } else {
    return Promise.resolve('other');
  }
}

export function launchPersonalMiniProgram() {
  const { yxIbuildAgentId } = getQywxConfig();
  return new Promise<any>((resolve, reject) => {
    wx.invoke(
      'launchMiniprogram',
      {
        agentid: yxIbuildAgentId.toString(), // 需跳转的小程序appid,感觉需要再三局通上关联小程序
        // appid: 'wx8994560be51e059f',
        path: '/pages/personal/index', // 所需跳转的小程序内页面路径及参数。非必填
      },
      function (res) {
        if (res.err_msg === 'launchMiniprogram:ok') {
          // 正常
          resolve(res);
        } else {
          Toast.fail(res.err_msg ?? 'err为空');
          // 错误处理
          reject(res.err_msg);
        }
      },
    );
  });
}

/**
 * 
【必填】APPID：通过明文 URL Scheme 打开小程序的 appid ；
【必填】PATH：通过明文 URL Scheme 打开小程序的页面 path ，必须是已经发布的小程序存在的页面，不可携带 query；
【选填】QUERY：通过明文 URL Scheme 打开小程序的 query ，最大512个字符，只支持数字，大小写英文以及部分特殊字符：!#$&'()*+,/:;=?@-._~%`，需要url_encode；
【选填】ENV_VERSION：要打开的小程序版本,正式版为release，体验版为trial，开发版为develop，仅在微信外打开时生效。注意：若不填写，则默认打开正式版小程序。
通过明文 URL Scheme 打开小程序的场景值为 1286。
注意这里path不需要编码,并且不能以「/」开头
weixin://dl/business/?appid=wx8994560be51e059f&path=pages/personal/index
 */
// 打开严选商城的小程序
const miniProgramAppId = 'wx8994560be51e059f';
export function buildMiniProgramSchema(path: string) {
  let targetMiniProgramPath = path;
  // 剔除掉开头的「/」
  if (path.startsWith('/')) targetMiniProgramPath = path.slice(1);
  return `weixin://dl/business/?appid=${miniProgramAppId}&path=${targetMiniProgramPath}`;
}
